import { useState } from 'react';
import { Modal } from '@/components/common/Modal';
import { Badge } from '@/components/data-display/Badge';
import { Button } from '@/components/common/Button';
import type { TransactionDto } from './types';
import {
  TRANSACTION_TYPE_LABELS,
  TRANSACTION_TYPE_BADGE_VARIANT,
  STATUS_BADGE_VARIANT,
} from './types';
import { formatAmount } from './format';

interface HistoryDetailModalProps {
  transaction: TransactionDto | null;
  isOpen: boolean;
  onClose: () => void;
}

export function HistoryDetailModal({ transaction, isOpen, onClose }: HistoryDetailModalProps) {
  const [showRawJson, setShowRawJson] = useState(false);

  if (!transaction) return null;

  const typeLabel =
    TRANSACTION_TYPE_LABELS[transaction.transaction_type] ?? transaction.transaction_type;
  const typeBadgeVariant = TRANSACTION_TYPE_BADGE_VARIANT[transaction.transaction_type] ?? 'info';
  const statusBadgeVariant = STATUS_BADGE_VARIANT[transaction.status] ?? 'default';

  return (
    <Modal
      isOpen={isOpen}
      title="Transaction Details"
      onClose={onClose}
      className="max-w-xl"
      footer={
        <Button variant="outline" onClick={onClose} data-testid="history-detail-close">
          Close
        </Button>
      }
    >
      <div className="space-y-4" data-testid="history-detail-content">
        {/* Header row */}
        <div className="flex items-center gap-3">
          <Badge variant={typeBadgeVariant}>{typeLabel}</Badge>
          <Badge variant={statusBadgeVariant}>{transaction.status}</Badge>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <DetailField label="Transaction ID" value={transaction.transaction_id} />
          <DetailField label="Type" value={typeLabel} />
          <DetailField
            label="Amount"
            value={formatAmount(transaction.amount, transaction.currency)}
          />
          <DetailField label="Currency" value={transaction.currency} />

          {/* FX-specific fields */}
          {transaction.transaction_type === 'fx_trade' && transaction.rate && (
            <>
              <DetailField label="Rate" value={transaction.rate} />
              {transaction.reference_currency && (
                <DetailField label="Reference Currency" value={transaction.reference_currency} />
              )}
            </>
          )}

          {/* Payment-specific fields */}
          {transaction.fee_applied && (
            <DetailField
              label="Fee Applied"
              value={formatAmount(transaction.fee_applied, transaction.currency)}
            />
          )}

          {/* Counterparty */}
          {transaction.beneficiary_name && (
            <DetailField label="Beneficiary" value={transaction.beneficiary_name} />
          )}
          {transaction.sender_name && (
            <DetailField label="Sender" value={transaction.sender_name} />
          )}

          {/* Dates */}
          <DetailField
            label="Created"
            value={new Date(transaction.created_at).toLocaleString('en-GB')}
          />
          <DetailField
            label="Updated"
            value={new Date(transaction.updated_at).toLocaleString('en-GB')}
          />

          {/* Description */}
          {transaction.description && (
            <div className="col-span-2">
              <DetailField label="Description" value={transaction.description} />
            </div>
          )}
        </div>

        {/* Raw JSON toggle (for operators) */}
        {transaction.raw_json && (
          <div className="border-t border-[#e8e8e8] pt-3">
            <button
              onClick={() => setShowRawJson(!showRawJson)}
              className="cursor-pointer border-none bg-transparent text-[12px] font-semibold text-[#6B0F1A] hover:underline"
              data-testid="history-detail-raw-toggle"
            >
              {showRawJson ? 'Hide Raw JSON' : 'Show Raw JSON'}
            </button>
            {showRawJson && (
              <pre
                className="mt-2 max-h-[300px] overflow-auto rounded-lg bg-[#f5f5f5] p-3 text-[11px] text-[#333]"
                data-testid="history-detail-raw-json"
              >
                {JSON.stringify(transaction.raw_json, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]">
        {label}
      </div>
      <div className="mt-0.5 text-[13px] text-[#333]">{value}</div>
    </div>
  );
}
